/**
 * Brand constants. One pink: the mark, the action color, and every accent
 * are the same #EC1A81 (tokens.css --action). The tagline appears on the
 * public pages (sign in, register, meta description) and never inside the
 * authenticated app.
 */
export const BRAND_NAME = 'keepr';
export const TAGLINE = 'Keep everything. Find anything.';
export const BRAND_PINK = '#EC1A81';

/**
 * The identity hues offered to users for collections, cards and tags — the
 * one definition every color picker reads (the icon + color modal, and the
 * color element's presets in components/common/ColorSwatch.js). Their
 * placement is restricted: an icon chip, and a 3px left edge on item cards —
 * nowhere else.
 *
 * Twelve hues around the wheel x two tones (KPR-313): a `base` row and a
 * `deep` row, in hue order. The six original swatches are the base tone of
 * their hue, at their original hex and under their original key, so items
 * already wearing one still show it as selected, and a tag that stored the
 * KEY ("emerald") still resolves (utils/tags.js tagColorOf). Never re-tint or
 * re-key those six; add beside them.
 *
 * `glyph` is the icon color that stays legible on the swatch: 'light' (white)
 * wherever white reaches 3:1, else 'dark' (#111). Declared, not left to the
 * generic luminance cut in utils/colors.js readableTextColor (> 0.45), which
 * put white on amber at 2.8:1 and on teal at 2.4:1; the identityHues test
 * holds every swatch to 3:1. The two glyph colors are constants, not theme
 * tokens: the tile is painted with the hue, so the pair reads the same in
 * every theme and mode.
 *
 * Deep tones are the base hue at ~92% of its saturation, darkened to a
 * relative luminance of ~0.075: dark enough to read as a distinct second row,
 * light enough that the hue is still recognisable (not ~0.03, where the deep
 * row collapsed into near-black and stopped telling hues apart).
 */
export const IDENTITY_HUES = [
	{ key: 'red', label: 'Red', value: '#E5484D', tone: 'base', glyph: 'light' },
	{ key: 'orange', label: 'Orange', value: '#EF6C1A', tone: 'base', glyph: 'light' },
	{ key: 'amber', label: 'Amber', value: '#D98A00', tone: 'base', glyph: 'dark' },
	{ key: 'lime', label: 'Lime', value: '#7FA614', tone: 'base', glyph: 'dark' },
	{ key: 'green', label: 'Green', value: '#2F9E44', tone: 'base', glyph: 'light' },
	{ key: 'emerald', label: 'Emerald', value: '#10A37F', tone: 'base', glyph: 'light' },
	{ key: 'teal', label: 'Teal', value: '#14B8C4', tone: 'base', glyph: 'dark' },
	{ key: 'blue', label: 'Blue', value: '#2F6FED', tone: 'base', glyph: 'light' },
	{ key: 'violet', label: 'Violet', value: '#7C5CE0', tone: 'base', glyph: 'light' },
	{ key: 'purple', label: 'Purple', value: '#A04DD8', tone: 'base', glyph: 'light' },
	{ key: 'fuchsia', label: 'Fuchsia', value: '#C93BB4', tone: 'base', glyph: 'light' },
	{ key: 'pink', label: 'Pink', value: '#EC1A81', tone: 'base', glyph: 'light' },
	{ key: 'red-deep', label: 'Deep red', value: '#971B1F', tone: 'deep', glyph: 'light' },
	{ key: 'orange-deep', label: 'Deep orange', value: '#7F390E', tone: 'deep', glyph: 'light' },
	{ key: 'amber-deep', label: 'Deep amber', value: '#6B4504', tone: 'deep', glyph: 'light' },
	{ key: 'lime-deep', label: 'Deep lime', value: '#41540E', tone: 'deep', glyph: 'light' },
	{ key: 'green-deep', label: 'Deep green', value: '#1E5829', tone: 'deep', glyph: 'light' },
	{ key: 'emerald-deep', label: 'Deep emerald', value: '#0C5846', tone: 'deep', glyph: 'light' },
	{ key: 'teal-deep', label: 'Deep teal', value: '#0D565B', tone: 'deep', glyph: 'light' },
	{ key: 'blue-deep', label: 'Deep blue', value: '#1547A8', tone: 'deep', glyph: 'light' },
	{ key: 'violet-deep', label: 'Deep violet', value: '#512DC1', tone: 'deep', glyph: 'light' },
	{ key: 'purple-deep', label: 'Deep purple', value: '#6F299F', tone: 'deep', glyph: 'light' },
	{ key: 'fuchsia-deep', label: 'Deep fuchsia', value: '#812874', tone: 'deep', glyph: 'light' },
	{ key: 'pink-deep', label: 'Deep pink', value: '#951353', tone: 'deep', glyph: 'light' },
];

/** The icon color each `glyph` names. Constants, not tokens: see IDENTITY_HUES. */
export const IDENTITY_GLYPH_COLORS = Object.freeze({ light: '#fff', dark: '#111' });
